import React from 'react';
import { FaEdit, FaImage, FaLink, FaTrash } from 'react-icons/fa';
import { Checkbox } from '../../form/checkbox.js';
import { SearchInput } from '../../search/search.Input.js';
import { ModalGlobal } from '../../modal/modal.Global.js';
import { BoardToolboxColorPalette } from '../boardToolboxColorPalette.js';
import { OpacitySlider } from '../../form/slider.Opacity.js';

export const BoardMapModals = props => {
  const {
    boardId,
    backgroundModalId,
    pinLinkModalId,
    measureSettingsModalId,
    gridSettingsModalId,
    backgroundFileInputRef,
    mapState,
    gridSizeInput,
    setGridSizeInput,
    onToggleGridVisible,
    onGridColorChange,
    onGridOpacityPreview,
    onGridOpacityCommit,
    onSaveGridSize,
    backgroundUrlInput,
    setBackgroundUrlInput,
    handleBackgroundFileChange,
    handleBackgroundUrlSubmit,
    handleClearBackground,
    onBackgroundOpacityPreview,
    onBackgroundOpacityCommit,
    isBackgroundSaving,
    backgroundError,
    selectedLinkTarget,
    updatePinPatch,
    updateShapePatch,
    updateRegionPatch,
    closeLinkModal,
    deletePins,
    pendingMeasureReference,
    measureScaleValueInput,
    setMeasureScaleValueInput,
    measureScaleUnitInput,
    setMeasureScaleUnitInput,
    measureScaleError,
    measureScales,
    activeMeasureScaleIds,
    onSaveMeasureScale,
    onToggleMeasureScale,
    onMultiplyMeasureScale,
    onUpdateMeasureScale,
    onCloseMeasureSettingsAndTakeMeasure,
    onDeleteMeasureScale,
  } = props;
  const [newScaleForm, setNewScaleForm] = React.useState({
    sourceScaleId: null,
    multiplier: '2',
    unit: '',
  });
  const [editScaleForm, setEditScaleForm] = React.useState({
    scaleId: null,
    value: '',
    unit: '',
    referencePx: '',
  });

  return (
    <>
      <ModalGlobal
        idToOpen={backgroundModalId}
        title='Image de fond'
        width={520}
      >
        <div className='flex flex-col gap-075'>
          <div className='flex items-center gap-05'>
            <label className='cursor-pointer rounded bg-primary px-1 py-05 text-xs text-contrast transition hover:opacity-80 flex items-center gap-05'>
              <FaImage size={12} />
              <span>Importer</span>
              <input
                ref={backgroundFileInputRef}
                type='file'
                accept='image/*'
                className='hidden'
                onChange={handleBackgroundFileChange}
              />
            </label>
            {mapState?.background?.url && (
              <button
                type='button'
                className='rounded bg-main-d bg-opacity-60 px-1 py-05 text-xs text-contrast transition hover:opacity-80 flex items-center gap-05'
                onClick={handleClearBackground}
                disabled={isBackgroundSaving}
              >
                <FaTrash size={10} />
                <span>Supprimer</span>
              </button>
            )}
          </div>
          <div className='flex items-center gap-05'>
            <input
              type='text'
              className='min-h-8 flex-1 rounded bg-main-d bg-opacity-40 px-1 py-05 text-xs text-contrast'
              placeholder='https://...'
              value={backgroundUrlInput}
              onChange={e => setBackgroundUrlInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleBackgroundUrlSubmit();
                }
              }}
            />
            <button
              type='button'
              className='rounded bg-primary px-1 py-05 text-xs text-contrast transition hover:opacity-80 flex items-center gap-05'
              onClick={handleBackgroundUrlSubmit}
              disabled={isBackgroundSaving || !backgroundUrlInput?.trim()}
            >
              <FaLink size={10} />
              <span>URL</span>
            </button>
          </div>
          {mapState?.background?.url && (
            <div className='rounded bg-main-d bg-opacity-40 p-1'>
              <div className='mb-05 flex items-center justify-between gap-05 text-xs font-semibold'>
                <span>Opacité</span>
                <span className='font-normal text-contrast text-opacity-70'>
                  {Math.round(
                    (Number.isFinite(Number(mapState?.background?.opacity))
                      ? Number(mapState.background.opacity)
                      : 1) * 100,
                  )}
                  %
                </span>
              </div>
              <OpacitySlider
                value={
                  Number.isFinite(Number(mapState?.background?.opacity))
                    ? Number(mapState.background.opacity)
                    : 1
                }
                min={0}
                max={1}
                step={0.01}
                onChange={onBackgroundOpacityPreview}
                onCommit={onBackgroundOpacityCommit}
              />
            </div>
          )}
          {backgroundError && (
            <div className='text-xs text-red'>{backgroundError}</div>
          )}
          {isBackgroundSaving && (
            <div className='text-xs text-contrast text-opacity-60'>
              Enregistrement du fond...
            </div>
          )}
        </div>
      </ModalGlobal>
      <ModalGlobal
        idToOpen={gridSettingsModalId}
        title='Grille'
        width={420}
      >
        <div className='flex flex-col gap-1 text-contrast'>
          <div className='flex items-center gap-05 text-xs'>
            <Checkbox
              size='sm'
              value={mapState?.grid?.visible === true}
              onChange={checked => onToggleGridVisible?.(checked === true)}
            />
            <span>Afficher la grille</span>
          </div>
          <div className='rounded bg-main-d bg-opacity-40 p-1'>
            <div className='mb-05 text-xs font-semibold'>Taille des carrés</div>
            <div className='flex items-center gap-05'>
              <input
                type='number'
                min='4'
                step='1'
                className='min-h-8 w-28 rounded bg-main-d bg-opacity-60 px-1 py-05 text-xs text-contrast'
                value={gridSizeInput}
                onChange={event => setGridSizeInput?.(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    onSaveGridSize?.();
                  }
                }}
              />
              <span className='text-xs text-contrast text-opacity-70'>px</span>
              <button
                type='button'
                className='rounded bg-primary px-1 py-05 text-xs text-contrast transition hover:opacity-80'
                onClick={onSaveGridSize}
              >
                Enregistrer
              </button>
            </div>
          </div>
          <div className='rounded bg-main-d bg-opacity-40 p-1'>
            <div className='mb-05 text-xs font-semibold'>Couleur</div>
            <BoardToolboxColorPalette
              value={mapState?.grid?.color || 'contrast'}
              onChange={onGridColorChange}
              titlePrefix='Couleur grille'
            />
          </div>
          <div className='rounded bg-main-d bg-opacity-40 p-1'>
            <div className='mb-05 flex items-center justify-between gap-05 text-xs font-semibold'>
              <div className='mb-05 text-xs font-semibold'>Opacité</div>
              <span className='font-normal text-contrast text-opacity-70'>
                {Math.round((Number(mapState?.grid?.opacity) || 0) * 100)}%
              </span>
            </div>
            <OpacitySlider
              value={
                Number.isFinite(Number(mapState?.grid?.opacity))
                  ? Number(mapState.grid.opacity)
                  : 0.16
              }
              min={0}
              max={1}
              step={0.01}
              onChange={onGridOpacityPreview}
              onCommit={onGridOpacityCommit}
            />
          </div>
        </div>
      </ModalGlobal>
      <ModalGlobal
        idToOpen={measureSettingsModalId}
        title='Réglage des distances'
        width={560}
      >
        <div className='flex flex-col gap-1 text-contrast'>
          <div className='rounded bg-main-d bg-opacity-40 p-1'>
            <div className='mb-075 flex items-center justify-between gap-05'>
              <div className='text-xs font-semibold text-contrast'>
                Référence en attente
              </div>
              <div
                className={`rounded px-05 py-px text-xs ${
                  pendingMeasureReference
                    ? 'bg-secondary text-main'
                    : 'bg-main bg-opacity-50 text-contrast text-opacity-70'
                }`}
              >
                {pendingMeasureReference ? (
                  'Prête à enregistrer'
                ) : (
                  <button
                    type='button'
                    className='rounded bg-primary px-1 py-05 text-xs text-contrast transition hover:opacity-80'
                    onClick={onCloseMeasureSettingsAndTakeMeasure}
                  >
                    Nouvelle mesure
                  </button>
                )}
              </div>
            </div>
            {pendingMeasureReference ? (
              <>
                <div className='mb-075 rounded bg-main bg-opacity-40 px-075 py-05 text-xs'>
                  Longueur tracée:{' '}
                  {Number(pendingMeasureReference.length).toFixed(1)} px
                </div>
                <div className='grid grid-cols-12 gap-05'>
                  <input
                    type='text'
                    inputMode='decimal'
                    className='col-span-5 min-h-8 rounded bg-main-d bg-opacity-60 px-1 py-05 text-xs text-contrast'
                    placeholder='Valeur'
                    value={measureScaleValueInput}
                    onChange={event =>
                      setMeasureScaleValueInput(event.target.value)
                    }
                  />
                  <input
                    type='text'
                    className='col-span-3 min-h-8 rounded bg-main-d bg-opacity-60 px-1 py-05 text-xs text-contrast'
                    placeholder='Unite'
                    value={measureScaleUnitInput}
                    onChange={event =>
                      setMeasureScaleUnitInput(event.target.value)
                    }
                  />
                  <div className='col-span-4 flex items-center justify-end gap-05'>
                    <button
                      type='button'
                      className='rounded bg-primary px-1 py-05 text-xs text-contrast transition hover:opacity-80'
                      onClick={onSaveMeasureScale}
                    >
                      Enregistrer
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className='text-xs text-contrast text-opacity-60'>
                Trace une ligne en mode réglage pour créer une référence.
              </div>
            )}
            {measureScaleError && (
              <div className='mt-075 rounded bg-red bg-opacity-20 px-075 py-05 text-xs text-red'>
                {measureScaleError}
              </div>
            )}
          </div>

          <div className='rounded bg-main-d bg-opacity-40 p-1'>
            <div className='mb-075 text-xs font-semibold text-contrast'>
              Échelles enregistrées
            </div>
            {measureScales?.length === 0 ? (
              <div className='text-xs text-contrast text-opacity-60'>
                Aucune échelle enregistrée.
              </div>
            ) : (
              <div className='flex flex-col gap-1'>
                {measureScales.map(scale => (
                  <div key={scale?.id}>
                    <div className='flex items-center gap-05'>
                      <Checkbox
                        size='sm'
                        value={activeMeasureScaleIds?.includes(scale?.id)}
                        onChange={() => onToggleMeasureScale(scale?.id)}
                        labelClassName='text-xs'
                      />
                      <div className='flex-1 text-xs'>
                        {scale?.value} {scale?.unit}
                        pour {Number(scale?.referencePx || 0).toFixed(1)} px
                      </div>
                      <button
                        type='button'
                        className='rounded bg-primary px-1 py-05 text-xs text-contrast transition hover:opacity-80'
                        onClick={() =>
                          setNewScaleForm({
                            sourceScaleId: scale?.id || null,
                            multiplier: '2',
                            unit: String(scale?.unit || '').trim(),
                          })
                        }
                      >
                        Nouvelle mesure
                      </button>
                      <button
                        type='button'
                        className='rounded bg-main-d bg-opacity-60 px-075 py-03 text-xs text-contrast transition hover:opacity-80'
                        onClick={() =>
                          setEditScaleForm({
                            scaleId: scale?.id || null,
                            value: String(scale?.value ?? ''),
                            unit: String(scale?.unit || ''),
                            referencePx: String(scale?.referencePx ?? ''),
                          })
                        }
                      >
                        <FaEdit size={11} />
                      </button>
                      <button
                        type='button'
                        className='rounded bg-secondary-d px-1 py-05 text-xs text-contrast transition hover:opacity-80'
                        onClick={() => onDeleteMeasureScale(scale?.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                    {newScaleForm.sourceScaleId === scale?.id && (
                      <div className='mt-075 rounded bg-main-d bg-opacity-50 p-075'>
                        <div className='mb-05 text-xs text-contrast text-opacity-70'>
                          Nouvelle mesure a partir de{' '}
                          {Number(scale?.value || 0)} {scale?.unit}
                        </div>
                        <div className='flex flex-wrap items-center gap-05'>
                          <span className='text-xs'>Valeur</span>
                          <span className='text-xs'>
                            {Number(scale?.value || 0)} ×
                          </span>
                          <input
                            type='text'
                            inputMode='decimal'
                            className='min-h-8 w-20 rounded bg-main-d bg-opacity-60 px-1 py-05 text-xs text-contrast'
                            value={newScaleForm.multiplier}
                            onChange={event =>
                              setNewScaleForm(prev => ({
                                ...prev,
                                multiplier: event.target.value,
                              }))
                            }
                          />
                          <span className='text-xs'>
                            ={' '}
                            {(() => {
                              const multiplier = Number(
                                String(newScaleForm.multiplier || '').replace(
                                  ',',
                                  '.',
                                ),
                              );
                              return Number.isFinite(multiplier) &&
                                multiplier > 0
                                ? Number(scale?.value || 0) * multiplier
                                : '?';
                            })()}
                          </span>
                          <span className='text-xs'>Unite</span>
                          <input
                            type='text'
                            className='min-h-8 w-20 rounded bg-main-d bg-opacity-60 px-1 py-05 text-xs text-contrast'
                            value={newScaleForm.unit}
                            onChange={event =>
                              setNewScaleForm(prev => ({
                                ...prev,
                                unit: event.target.value,
                              }))
                            }
                          />
                          <button
                            type='button'
                            className='rounded bg-primary px-1 py-05 text-xs text-contrast transition hover:opacity-80'
                            onClick={() => {
                              const multiplier = Number(
                                String(newScaleForm.multiplier || '').replace(
                                  ',',
                                  '.',
                                ),
                              );
                              if (
                                !Number.isFinite(multiplier) ||
                                multiplier <= 0
                              ) {
                                return;
                              }
                              onMultiplyMeasureScale?.(
                                scale?.id,
                                multiplier,
                                newScaleForm.unit,
                              );
                              setNewScaleForm({
                                sourceScaleId: null,
                                multiplier: '2',
                                unit: '',
                              });
                            }}
                          >
                            Créer
                          </button>
                          <button
                            type='button'
                            className='rounded bg-main-d bg-opacity-60 px-1 py-05 text-xs text-contrast transition hover:opacity-80'
                            onClick={() =>
                              setNewScaleForm({
                                sourceScaleId: null,
                                multiplier: '2',
                                unit: '',
                              })
                            }
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}
                    {editScaleForm.scaleId === scale?.id && (
                      <div className='mt-075 rounded bg-main-d bg-opacity-50 p-075'>
                        <div className='mb-05 text-xs text-contrast text-opacity-70'>
                          Modifier la mesure
                        </div>
                        <div className='flex flex-wrap items-center gap-05'>
                          <span className='text-xs'>Valeur</span>
                          <input
                            type='text'
                            inputMode='decimal'
                            className='min-h-8 w-20 rounded bg-main-d bg-opacity-60 px-1 py-05 text-xs text-contrast'
                            value={editScaleForm.value}
                            onChange={event =>
                              setEditScaleForm(prev => ({
                                ...prev,
                                value: event.target.value,
                              }))
                            }
                          />
                          <span className='text-xs'>Unite</span>
                          <input
                            type='text'
                            className='min-h-8 w-20 rounded bg-main-d bg-opacity-60 px-1 py-05 text-xs text-contrast'
                            value={editScaleForm.unit}
                            onChange={event =>
                              setEditScaleForm(prev => ({
                                ...prev,
                                unit: event.target.value,
                              }))
                            }
                          />
                          <span className='text-xs'>Référence px</span>
                          <input
                            type='text'
                            inputMode='decimal'
                            className='min-h-8 w-20 rounded bg-main-d bg-opacity-60 px-1 py-05 text-xs text-contrast'
                            value={editScaleForm.referencePx}
                            onChange={event =>
                              setEditScaleForm(prev => ({
                                ...prev,
                                referencePx: event.target.value,
                              }))
                            }
                          />
                          <button
                            type='button'
                            className='rounded bg-primary px-1 py-05 text-xs text-contrast transition hover:opacity-80'
                            onClick={() => {
                              onUpdateMeasureScale?.(scale?.id, {
                                value: editScaleForm.value,
                                unit: editScaleForm.unit,
                                referencePx: editScaleForm.referencePx,
                              });
                              setEditScaleForm({
                                scaleId: null,
                                value: '',
                                unit: '',
                                referencePx: '',
                              });
                            }}
                          >
                            Enregistrer
                          </button>
                          <button
                            type='button'
                            className='rounded bg-main-d bg-opacity-60 px-1 py-05 text-xs text-contrast transition hover:opacity-80'
                            onClick={() =>
                              setEditScaleForm({
                                scaleId: null,
                                value: '',
                                unit: '',
                                referencePx: '',
                              })
                            }
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </ModalGlobal>
      <ModalGlobal
        idToOpen={pinLinkModalId}
        title={
          selectedLinkTarget?.type === 'shape'
            ? 'Lier un item a la forme'
            : selectedLinkTarget?.type === 'region'
              ? 'Lier un item a la region'
              : 'Lier un item au pin'
        }
        width={520}
      >
        <div className='text-xs text-contrast text-opacity-70 mb-1'>
          {selectedLinkTarget
            ? `Choisis un item a lier a ce${
                selectedLinkTarget.type === 'region'
                  ? 'tte region'
                  : selectedLinkTarget.type === 'shape'
                    ? 'tte forme'
                    : ' pin'
              }.`
            : 'Aucun element selectionne.'}
        </div>
        {selectedLinkTarget && (
          <>
            <SearchInput
              itemId={boardId}
              searchIn={{
                type: true,
                universe: true,
                world: true,
                campaign: true,
              }}
              className='text-xs'
              showNavigateIcon={false}
              callback={searchedItem => {
                if (selectedLinkTarget.type === 'pin') {
                  updatePinPatch(selectedLinkTarget.entity.id, {
                    linkedItemId: searchedItem?._id || null,
                    label:
                      searchedItem?.label ||
                      selectedLinkTarget.entity?.label ||
                      '',
                  });
                } else if (selectedLinkTarget.type === 'shape') {
                  updateShapePatch(selectedLinkTarget.entity.id, {
                    linkedItemId: searchedItem?._id || null,
                    label:
                      searchedItem?.label ||
                      selectedLinkTarget.entity?.label ||
                      '',
                  });
                } else {
                  updateRegionPatch(selectedLinkTarget.entity.id, {
                    linkedItemId: searchedItem?._id || null,
                    label:
                      searchedItem?.label ||
                      selectedLinkTarget.entity?.label ||
                      '',
                  });
                }
                closeLinkModal();
              }}
            />
            {selectedLinkTarget?.entity?.linkedItemId && (
              <div className='mt-1 flex items-center gap-05'>
                <button
                  type='button'
                  className='rounded bg-main-d bg-opacity-60 px-1 py-05 text-xs text-contrast transition hover:opacity-80'
                  onClick={() => {
                    if (selectedLinkTarget.type === 'pin') {
                      updatePinPatch(selectedLinkTarget.entity.id, {
                        linkedItemId: null,
                      });
                    } else if (selectedLinkTarget.type === 'shape') {
                      updateShapePatch(selectedLinkTarget.entity.id, {
                        linkedItemId: null,
                      });
                    } else {
                      updateRegionPatch(selectedLinkTarget.entity.id, {
                        linkedItemId: null,
                      });
                    }
                    closeLinkModal();
                  }}
                >
                  Retirer le lien
                </button>
                {selectedLinkTarget.type === 'pin' && (
                  <button
                    type='button'
                    className='rounded bg-red bg-opacity-80 px-1 py-05 text-xs text-contrast transition hover:opacity-80'
                    onClick={() => deletePins([selectedLinkTarget.entity.id])}
                  >
                    Supprimer le pin
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </ModalGlobal>
    </>
  );
};
